import React, { createContext, useContext, useState, useEffect } from "react";
import { fetchMe as fetchCustomerMe } from "@/services/customerService";
import { fetchMe as fetchOwnerMe } from "@/services/dashboardService";

type CurrentUser = {
  id: number;
  email: string;
  pending_email?: string | null;
  email_verified?: boolean; // メールアドレスが認証済みか
  otp_enabled?: boolean; // 二段階認証が有効か
  balance?: number; // `balance` を追加
  sites?: Array<{
    id: number;
    name: string;
    subdomain: string;
    site_url?: string;
    site_type?: "oripa" | "kuji";
    role: "owner" | "member";
    permissions: string[];
  }>;
  is_admin?: boolean; // `admin` を追加
  is_owner?: boolean; // `is_owner` を追加
  birth_date?: string | null; // 生年月日
  birth_date_verified?: boolean; // 生年月日が登録済みか
  is_adult?: boolean; // 18歳以上か
  age?: number | null; // 年齢
  phone_number?: string | null; // 電話番号
  phone_verified?: boolean; // 電話番号が認証済みか
  lottery_animation_enabled?: boolean; // オリパ演出動画を再生するか
  oauth_user?: boolean; // OAuth認証ユーザーか
  google_user?: boolean; // Google認証ユーザーか
} | null;

type AuthContextType = {
  currentUser: CurrentUser;
  authLoading: boolean;
  refreshUser: () => Promise<void>;
  login: (data: { token: string }) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser>(() => {
    if (!localStorage.getItem("authToken")) return null;

    const storedUser = localStorage.getItem("currentUser");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [authLoading, setAuthLoading] = useState(() => Boolean(localStorage.getItem("authToken")));

  const refreshUser = async (logoutOnError = true) => {
    try {
      // Dashboard かどうかを判定
      const isDashboard = window.location.hostname.includes('dashboard') || window.location.pathname.startsWith('/dashboard');
      const fetchMe = isDashboard ? fetchOwnerMe : fetchCustomerMe;

      const { current_user } = await fetchMe();
      setCurrentUser(current_user);
      localStorage.setItem("currentUser", JSON.stringify(current_user)); // localStorage に保存
    } catch (error) {
      console.error("[AuthContext] refreshUser: エラー発生", error);
      if (logoutOnError) {
        logout(); // 失敗したらログアウト
      } else {
        throw error; // ログインフロー中はエラーを再スロー
      }
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("authToken")) {
      refreshUser(); // 初回読み込み時に `fetchMe` を実行
    }
  }, []);

  const login = async (data: { token: string }) => {
    localStorage.setItem("authToken", data.token);
    await refreshUser(false); // ログインフロー中はエラー時にログアウトしない
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("currentUser");
    setCurrentUser(null);
    window.location.href = "/login"; // navigateの代わりにwindow.location.hrefを使用
  };

  return (
    <AuthContext.Provider value={{ currentUser, authLoading, refreshUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
